import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Shield, Plus, Edit, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useSystemRoles } from '@/hooks/useSystemRoles';
import { useDepartments } from '@/hooks/useDepartments';
import { useQueryClient } from '@tanstack/react-query';

const RESOURCES = ['clients', 'cases', 'leads', 'documents', 'payments'];
const ACTIONS = ['create', 'read', 'update', 'delete'];
const SCOPES = [
  { value: 'all', label: 'Todos' },
  { value: 'department', label: 'Departamento' },
  { value: 'team', label: 'Equipe' },
  { value: 'assigned', label: 'Atribuídos' },
  { value: 'own', label: 'Próprios' },
  { value: 'own_or_unassigned', label: 'Próprios ou Não Atribuídos' },
  { value: 'related', label: 'Relacionados' },
  { value: 'none', label: 'Nenhum' },
];

export default function SystemRolesSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { roles, isLoading } = useSystemRoles();
  const { departments, isLoading: isLoadingDepartments } = useDepartments();
  const [showDialog, setShowDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    department_id: '',
    is_active: true,
    permissions: {},
  });

  const initializePermissions = () => {
    const perms = {};
    RESOURCES.forEach((resource) => {
      perms[resource] = {
        create: false,
        read: false,
        update: false,
        delete: false,
        scope: 'none',
      };
    });
    return perms;
  };

  const handleOpenNew = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      department_id: '',
      is_active: true,
      permissions: initializePermissions(),
    });
    setShowDialog(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      code: role.code,
      description: role.description || '',
      department_id: role.department_id || '',
      is_active: role.is_active,
      permissions: role.permissions || initializePermissions(),
    });
    setShowDialog(true);
  };

  const handlePermissionChange = (resource, action, value) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [resource]: {
          ...prev.permissions[resource],
          [action]: value,
        },
      },
    }));
  };

  const handleScopeChange = (resource, scope) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [resource]: {
          ...prev.permissions[resource],
          scope,
        },
      },
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Nome e Código são obrigatórios');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRole) {
        // Update existing
        const { error } = await supabase
          .from('system_roles')
          .update({
            name: formData.name.trim(),
            code: formData.code.trim().toLowerCase(),
            description: formData.description.trim() || null,
            department_id: formData.department_id || null,
            is_active: formData.is_active,
            permissions: formData.permissions,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingRole.id);

        if (error) throw error;
        toast.success('Função atualizada com sucesso');
      } else {
        // Create new
        const { error } = await supabase.from('system_roles').insert({
          name: formData.name.trim(),
          code: formData.code.trim().toLowerCase(),
          description: formData.description.trim() || null,
          department_id: formData.department_id || null,
          is_active: formData.is_active,
          permissions: formData.permissions,
        });

        if (error) throw error;
        toast.success('Função criada com sucesso');
      }

      queryClient.invalidateQueries(['system-roles']);
      setShowDialog(false);
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.message || 'Falha ao salvar função');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (role) => {
    try {
      const { error } = await supabase
        .from('system_roles')
        .update({ is_active: !role.is_active, updated_at: new Date().toISOString() })
        .eq('id', role.id);

      if (error) throw error;
      queryClient.invalidateQueries(['system-roles']);
      toast.success(`Função ${!role.is_active ? 'ativada' : 'desativada'}`);
    } catch (error) {
      console.error('Error toggling role:', error);
      toast.error('Falha ao atualizar função');
    }
  };

  return (
    <div className="bg-background p-6 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(createPageUrl('Settings'))}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Funções do Sistema</h1>
              <p className="text-muted-foreground">Gerencie funções e permissões</p>
            </div>
          </div>
          <Button onClick={handleOpenNew} className="gap-2">
            <Plus className="w-4 h-4" />
            Nova Função
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Todas as Funções</CardTitle>
                <CardDescription>
                  Visualize e gerencie funções do sistema com permissões granulares
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando funções...</div>
            ) : roles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma função encontrada. Crie sua primeira função para começar.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{role.code}</code>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-md truncate">
                        {role.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={role.is_active ? 'default' : 'secondary'}>
                          {role.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(role)}
                            className="gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Editar
                          </Button>
                          <Button
                            variant={role.is_active ? 'secondary' : 'default'}
                            size="sm"
                            onClick={() => handleToggleActive(role)}
                          >
                            {role.is_active ? 'Desativar' : 'Ativar'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Editar Função' : 'Nova Função'}</DialogTitle>
              <DialogDescription>
                {editingRole
                  ? 'Atualize as informações e permissões da função abaixo.'
                  : 'Crie uma nova função com permissões específicas.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Nome da Função <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ex: Gerente de Vendas, Admin TI"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="code">
                    Código <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toLowerCase() })
                    }
                    placeholder="ex: gerente_vendas, admin_ti"
                  />
                  <p className="text-xs text-muted-foreground">
                    Identificador único (minúsculas, use sublinhados)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Breve descrição da função"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Departamento (Opcional)</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(value) => 
                      setFormData({ ...formData, department_id: value === 'none' ? '' : value })
                    }
                  >
                    <SelectTrigger id="department">
                      <SelectValue placeholder="Selecione o departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem Departamento (Global)</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Funções sem departamento são globais e se aplicam a todos os departamentos
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="is_active">Ativo</Label>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                </div>
              </div>

              {/* Permissions Matrix */}
              <div className="space-y-4">
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Matriz de Permissões</h3>
                  <div className="space-y-4">
                    {RESOURCES.map((resource) => (
                      <Card key={resource}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base capitalize">{resource}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-4 gap-4">
                            {ACTIONS.map((action) => (
                              <div key={action} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`${resource}-${action}`}
                                  checked={
                                    formData.permissions[resource]?.[action] || false
                                  }
                                  onCheckedChange={(checked) =>
                                    handlePermissionChange(resource, action, checked)
                                  }
                                />
                                <Label
                                  htmlFor={`${resource}-${action}`}
                                  className="text-sm capitalize cursor-pointer"
                                >
                                  {action}
                                </Label>
                              </div>
                            ))}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Escopo</Label>
                            <Select
                              value={formData.permissions[resource]?.scope || 'none'}
                              onValueChange={(value) => handleScopeChange(resource, value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SCOPES.map((scope) => (
                                  <SelectItem key={scope.value} value={scope.value}>
                                    {scope.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Salvando...' : editingRole ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
